import React, { useEffect, useRef, useMemo } from 'react';
import { evaluate } from 'mathjs';
import { NumberInput } from 'src/views/trans/JCdTrans/components/NumberInput';

const AutoNumberWithFormula = ({ data, onChangeField }) => {
  const rawExp = data?.exp || data?.formula || '';
  const formattedExp =
    rawExp && !rawExp.startsWith('=') ? `=${rawExp}` : rawExp;
  const prevValueRef = useRef(data?.value);

  function handleChangeField({ value }) {
    onChangeField({
      target: {
        name: data.field,
        value: +value
      }
    });
  }

  // 1. Extract variable names used in rawExp
  const usedVariables = useMemo(() => {
    if (!rawExp) return [];
    const cleanExp = rawExp.startsWith('=') ? rawExp.slice(1) : rawExp;
    // Match standard JavaScript/Math variable identifiers (words not starting with digits)
    const matches = cleanExp.match(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g) || [];
    return Array.from(new Set(matches)); // Deduplicate
  }, [rawExp]);

  // 2. Build a sub-object containing ONLY the relevant variable values
  const relevantVariables = useMemo(() => {
    const allVars = data?.variables || {};
    const filtered = {};
    usedVariables.forEach(varName => {
      if (varName in allVars) {
        filtered[varName] = allVars[varName];
      }
    });
    return filtered;
  }, [usedVariables, data?.variables]);

  // 3. Serialize ONLY the relevant variables for strict effect triggering
  const relevantVarsKey = JSON.stringify(relevantVariables);

  // Reactive auto-computation: Fires ONLY when variables referenced in formula change
  useEffect(() => {
    if (!rawExp || !data?.variables) return;

    try {
      const cleanExp = rawExp.startsWith('=') ? rawExp.slice(1) : rawExp;
      const calculatedResult = evaluate(cleanExp, data.variables);

      if (
        typeof calculatedResult === 'number' &&
        !isNaN(calculatedResult) &&
        calculatedResult !== prevValueRef.current
      ) {
        prevValueRef.current = calculatedResult;

        onChangeField({
          target: {
            name: data.field,
            value: +calculatedResult
          }
        });
      }
    } catch (error) {
      // Ignore intermediate math errors while typing
    }
  }, [relevantVarsKey, rawExp]);

  return (
    <NumberInput
      value={data.value}
      exp={formattedExp}
      variables={data?.variables || {}}
      excludedVariables={data?.excludedVariables || []}
      balanceShortcut={data?.balanceShortcut ?? true}
      label={data?.label || data?.caption || ''}
      error={data?.error ?? false}
      errorMessage={data?.errorMessage || ''}
      onChange={handleChangeField}
      disableDecimal={data?.disableDecimal ?? false}
      format={data?.format ?? '0,0.00'}
      textboxProps={{ disabled: data.disabled, ...data.textboxProps }}
    />
  );
};

export default AutoNumberWithFormula;
